import { Deserializable } from '../../protocols/deserializable';
import { SubmissionStatusIdEnum } from '../../enum/shared/submission-status-id.enum';
import { SubmissionSubStatusIdEnum } from '../../enum/shared/submission-sub-status-id-enum';
import { SubmissionSubStatus } from '../../shared/submission-status';
import { SubmissionComment } from '../submission-comment';
import { SubmissionReminder, SubmissionStakeholder } from '../member-submission';
import { SubmissionHistoryItem } from '../submission-history-item';
import { PortalTypeIdEnum } from '../../enum/shared/portal-type-id.enum';
import { SubmissionStatusPill } from '../../shared/submission-status-pill';
import * as moment from 'moment';
import { MemberSubmissionStatus } from '../member-submission-status';
import { EmployerSubmissionTypeEnum } from '../../enum/shared/employer-submission-type.enum';
import { EmployerSubmissionAttachment } from './employer-submission-attachment';
import { EmployerSelfEnrolmentSubmission } from './submission-bodies/employer-self-enrol/employer-self-enrolment-submission';
import { EmployerUpdateAddressSubmission } from './submission-bodies/employer-self-enrol/employer-update-address-submission';
import { UpdateEmployerContactsSubmission } from './submission-bodies/update-contacts/update-employer-contacts-submission';
import { UpdateEmploymentStatusSubmission } from './submission-bodies/update-employment-status/update-employment-status-submission';

export class IpEmployerSubmission implements Deserializable {
  public id!: number;
  public employerId!: number;
  public employerName!: string;
  public submissionTypeId!: EmployerSubmissionTypeEnum;
  public submissionTypeName!: string;
  public statusId!: SubmissionStatusIdEnum;
  public status!: MemberSubmissionStatus;
  public subStatusId!: SubmissionSubStatusIdEnum;
  public subStatus!: SubmissionSubStatus;
  public comments!: SubmissionComment[];
  public ownerId!: number | null;
  public owner!: SubmissionStakeholder;
  public approverId!: number | null;
  public approver!: SubmissionStakeholder;
  public history!: SubmissionHistoryItem[];
  public idPrefix!: string;
  public idSuffix!: string;
  public submittedDate!: string;
  public overdueDate!: string;
  public reminder!: SubmissionReminder | null;
  public expirationDate!: string;
  public lastStatusUpdateDate!: string;
  public createdInPortalTypeId!: PortalTypeIdEnum;
  public completedDate!: string;
  public createdDate!: string;
  public attachments!: EmployerSubmissionAttachment[];
  public createdByEmployerUserName!: string | null;
  public selfEnrolmentSubmissionId!: string | null;
  public selfEnrolmentSubmission!: EmployerSelfEnrolmentSubmission | null;
  public updateAddressSubmissionId!: string | null;
  public updateAddressSubmission!: EmployerUpdateAddressSubmission | null;
  public updateContactsSubmissionId!: string | null;
  public updateContactsSubmission!: UpdateEmployerContactsSubmission;
  public updateEmploymentStatusSubmissionId!: string | null;
  public updateEmploymentStatusSubmission!: UpdateEmploymentStatusSubmission | null;

  // Not From API
  public submissionId!: string;
  public formattedExpirationDate!: string;
  public expiringSoon!: boolean;
  public statusPill!: SubmissionStatusPill;
  public completedDateString!: string;
  public createdDateString!: string;

  public canUploadFiles(): boolean {
    return this.statusId === SubmissionStatusIdEnum.Submitted || this.statusId === SubmissionStatusIdEnum.InReview;
  }

  public canChangeSubmissionOwner(): boolean {
    return (
      this.subStatusId === SubmissionSubStatusIdEnum.Ready ||
      this.subStatusId === SubmissionSubStatusIdEnum.PendingDocuments ||
      this.subStatusId === SubmissionSubStatusIdEnum.Processing ||
      this.subStatusId === SubmissionSubStatusIdEnum.MemberActionRequired ||
      this.subStatusId === SubmissionSubStatusIdEnum.EmployerActionRequired ||
      this.subStatusId === SubmissionSubStatusIdEnum.ReadyForApproval
    );
  }

  public canChangeSubmissionApprover(): boolean {
    return this.subStatusId === SubmissionSubStatusIdEnum.ReadyForApproval;
  }

  public isApproved(): boolean {
    // @ts-ignore
    return [SubmissionSubStatusIdEnum.Approved, SubmissionSubStatusIdEnum.AutoApproved].contains(this.subStatusId ?? 0);
  }

  public onDeserialize() {
    const Deserialize = window?.injector?.Deserialize;
    this.submissionId = this.buildSubmissionId();

    if (!!this.owner) {
      this.owner = Deserialize?.instanceOf(SubmissionStakeholder, this.owner);
    }
    if (!!this.approver) {
      this.approver = Deserialize?.instanceOf(SubmissionStakeholder, this.approver);
    }
    if (!!this.comments) {
      this.comments = Deserialize?.arrayOf(SubmissionComment, this.comments);
    }
    if (!!this.reminder) {
      this.reminder = Deserialize?.instanceOf(SubmissionReminder, this.reminder);
    }
    if (!!this.history) {
      this.history = Deserialize?.arrayOf(SubmissionHistoryItem, this.history);
      this.history.sort((a, b) => new Date(b.eventDate ?? '').getTime() - new Date(a.eventDate ?? '').getTime());
    }
    if (!!this.status) {
      this.status = Deserialize?.instanceOf(MemberSubmissionStatus, this.status);
      this.statusPill = new SubmissionStatusPill(
        this.subStatusId,
        this.subStatus.name,
        this.statusId,
        this.status.name
      );
    }
    if (!!this.subStatus) {
      this.subStatus = Deserialize?.instanceOf(SubmissionSubStatus, this.subStatus);
    }
    if (!!this.attachments) {
      this.attachments = Deserialize?.arrayOf(EmployerSubmissionAttachment, this.attachments);
    }

    if (!!this.expirationDate) {
      this.formattedExpirationDate = moment(this.expirationDate).format('MMMM D, YYYY');
      // if the submission is expiring within the next 10 days, set expiringSoon to true
      this.expiringSoon = moment(this.expirationDate).diff(moment(), 'days') <= 10;
    }

    if (!!this.completedDate) {
      this.completedDateString = this.completedDate;
    }

    if (!!this.createdDate) {
      this.createdDateString = this.createdDate;
    }

    if (!!this.selfEnrolmentSubmission) {
      this.selfEnrolmentSubmission = Deserialize?.instanceOf(
        EmployerSelfEnrolmentSubmission,
        this.selfEnrolmentSubmission
      );
    }

    if (!!this.updateAddressSubmission) {
      this.updateAddressSubmission = Deserialize?.instanceOf(
        EmployerUpdateAddressSubmission,
        this.updateAddressSubmission
      );
    }

    if (!!this.updateContactsSubmission) {
      this.updateContactsSubmission = Deserialize?.instanceOf(
        UpdateEmployerContactsSubmission,
        this.updateContactsSubmission
      );
    }

    if (this.updateEmploymentStatusSubmission) {
      this.updateEmploymentStatusSubmission = Deserialize?.instanceOf(
        UpdateEmploymentStatusSubmission,
        this.updateEmploymentStatusSubmission
      );
    }
  }

  private buildSubmissionId(): string {
    // Submission IDs should be 8 characters long, padded with leading zeros
    const paddedId = this.id.toString().padStart(8, '0');
    return `${this.idPrefix}-${paddedId}-${this.idSuffix}`;
  }
}
